import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { guardarDestino } from '@/lib/destino';
import Cabecalho from '@/components/ui/Cabecalho';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import CampoArea from '@/components/base/CampoArea';

const WHATSAPP_URL =
  'https://wa.me/5514996402835?text=Ol%C3%A1!%20Quero%20excluir%20minha%20conta%20do%20Se%20Liga.';

type Estado = 'carregando' | 'deslogado' | 'sem-pedido' | 'com-pedido' | 'erro';

interface RespostaMinha {
  pedido?: boolean;
  protocolo?: string | null;
  pedido_em?: string | null;
}

function dataBR(iso?: string | null): string {
  if (!iso) return '';
  const [a, m, d] = iso.slice(0, 10).split('-');
  if (!a || !m || !d) return '';
  return `${d}/${m}/${a}`;
}

function CardInfo({
  icone,
  titulo,
  children,
}: {
  icone: string;
  titulo: string;
  children: ReactNode;
}) {
  return (
    <Cartao className="p-5 md:p-6">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
          <i className={`${icone} text-lg`} />
        </span>
        <div className="min-w-0">
          <p className="text-base font-bold text-foreground-950">{titulo}</p>
          <p className="mt-1 text-base leading-relaxed text-foreground-700">{children}</p>
        </div>
      </div>
    </Cartao>
  );
}

export default function ExcluirContaPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [protocolo, setProtocolo] = useState<string | null>(null);
  const [pedidoEm, setPedidoEm] = useState<string | null>(null);

  const [motivo, setMotivo] = useState('');
  const [ciente, setCiente] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);
  const [recadoErro, setRecadoErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setEstado('carregando');
    setErro(null);
    try {
      if (!pronto) return;
      if (!user) {
        setEstado('deslogado');
        return;
      }
      const { data, error } = await supabase.rpc('minha_exclusao');
      if (error) throw error;
      const d = (data ?? {}) as RespostaMinha;
      if (d.pedido) {
        setProtocolo(d.protocolo ?? null);
        setPedidoEm(d.pedido_em ?? null);
        setEstado('com-pedido');
      } else {
        setEstado('sem-pedido');
      }
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setEstado('erro');
    }
  }, [pronto, user]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  function entrar() {
    guardarDestino('/excluir-conta');
    navigate('/entrar');
  }

  async function pedir() {
    setEnviando(true);
    setRecadoErro(null);
    try {
      const { data, error } = await supabase.rpc('pedir_exclusao_conta', {
        p_motivo: motivo.trim() || null,
      });
      if (error) throw error;
      const d = (data ?? {}) as { ok?: boolean; protocolo?: string | null; recado?: string | null };
      setRecado(d.recado ?? 'Seu pedido foi registrado.');
      if (d.protocolo) setProtocolo(d.protocolo);
      await carregar();
    } catch (e) {
      setRecadoErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  async function desistir() {
    setEnviando(true);
    setRecadoErro(null);
    try {
      const { data, error } = await supabase.rpc('cancelar_exclusao_conta');
      if (error) throw error;
      const d = (data ?? {}) as { recado?: string | null };
      setRecado(d.recado ?? 'Pedido cancelado.');
      await carregar();
    } catch (e) {
      setRecadoErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Cabecalho titulo="Excluir minha conta" larguraMaxima="max-w-[720px]" />

      <main className="mx-auto w-full max-w-[720px] px-4 py-8 md:px-6 md:py-10">
        <p className="text-base leading-relaxed text-foreground-700">
          Você pode pedir a exclusão da sua conta do Se Liga Católico a qualquer momento. Vamos
          concluir em até 15 dias e avisar você.
        </p>

        <div className="mt-6 space-y-3">
          <CardInfo icone="ri-delete-bin-6-line" titulo="O que é apagado">
            Seu nome, e-mail, CPF, telefone, endereço, foto e as respostas das suas fichas.
          </CardInfo>
          <CardInfo icone="ri-shield-keyhole-line" titulo="O que precisamos guardar por lei">
            O registro dos pagamentos (valor, data e evento), sem os seus dados pessoais, pelo
            prazo exigido pela legislação fiscal. Ingressos e inscrições já usados deixam de
            mostrar o seu nome.
          </CardInfo>
          <CardInfo icone="ri-information-line" titulo="Antes de pedir">
            Se você é organizador de um evento em andamento, fale com a gente primeiro.
          </CardInfo>
        </div>

        {recado && (
          <div className="mt-6 rounded-2xl border border-success-200 bg-success-50 px-4 py-3">
            <p className="text-sm font-medium text-success-800">{recado}</p>
          </div>
        )}
        {recadoErro && (
          <div className="mt-6 rounded-2xl border border-danger-200 bg-danger-50 px-4 py-3">
            <p className="text-sm text-danger-700">{recadoErro}</p>
          </div>
        )}

        <div className="mt-6">
          {estado === 'carregando' && (
            <div className="space-y-3">
              <div className="h-12 w-full animate-pulse rounded-2xl bg-secondary-100" />
              <div className="h-12 w-2/3 animate-pulse rounded-2xl bg-secondary-100" />
            </div>
          )}

          {estado === 'erro' && (
            <Cartao className="p-6 text-center">
              <p className="text-base font-semibold text-foreground-950">
                Não foi possível carregar
              </p>
              <p className="mt-1 text-sm text-foreground-600">{erro}</p>
              <div className="mt-5 flex justify-center">
                <Botao variante="primario" icone="ri-refresh-line" onClick={() => void carregar()}>
                  Tentar de novo
                </Botao>
              </div>
            </Cartao>
          )}

          {estado === 'deslogado' && (
            <Cartao className="p-5 md:p-6">
              <p className="text-sm text-foreground-600">
                Entre na sua conta para pedir a exclusão. Se preferir, peça pelo WhatsApp.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <Botao variante="primario" icone="ri-login-box-line" onClick={entrar}>
                  Entrar para pedir a exclusão
                </Botao>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-[52px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-[#25D366] px-6 text-base font-semibold text-white transition-all hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                >
                  <span className="flex h-5 w-5 items-center justify-center">
                    <i className="ri-whatsapp-line" />
                  </span>
                  Prefiro pedir pelo WhatsApp
                </a>
              </div>
            </Cartao>
          )}

          {estado === 'sem-pedido' && (
            <Cartao className="p-5 md:p-6">
              <CampoArea
                id="motivo-exclusao"
                name="motivo"
                label="Quer contar o motivo? (opcional)"
                value={motivo}
                onChange={setMotivo}
                placeholder="Se quiser, conte por que está saindo."
                maxLength={500}
                simples
              />

              <label className="mt-4 flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={ciente}
                  onChange={(e) => setCiente(e.target.checked)}
                  className="mt-1 h-5 w-5 shrink-0 cursor-pointer rounded border-secondary-300 text-primary-600 focus:ring-primary-400"
                />
                <span className="text-sm text-foreground-800">
                  Entendo que isso não pode ser desfeito depois de concluído
                </span>
              </label>

              <div className="mt-5">
                <Botao
                  variante="perigo"
                  tamanho="lg"
                  cheio
                  icone="ri-delete-bin-6-line"
                  disabled={!ciente}
                  carregando={enviando}
                  onClick={() => void pedir()}
                >
                  Pedir exclusão da conta
                </Botao>
              </div>
            </Cartao>
          )}

          {estado === 'com-pedido' && (
            <Cartao className="p-5 md:p-6">
              <p className="text-base font-semibold text-foreground-950">
                {protocolo
                  ? `Seu pedido nº ${protocolo} foi feito em ${dataBR(pedidoEm)}.`
                  : 'Seu pedido de exclusão está aberto.'}
              </p>
              <p className="mt-1 text-sm text-foreground-600">
                Vamos concluir em até 15 dias.
              </p>
              <div className="mt-5">
                <Botao
                  variante="fantasma"
                  icone="ri-arrow-go-back-line"
                  carregando={enviando}
                  onClick={() => void desistir()}
                >
                  Desistir do pedido
                </Botao>
              </div>
            </Cartao>
          )}
        </div>
      </main>
    </div>
  );
}